import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StudioSidebar from '../../components/ui/StudioSidebar';

const StudioRevenue = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    adRevenue: 0,
    iapRevenue: 0,
    payouts: []
  });

  useEffect(() => {
    // Allow both studio_admin and admin roles
    if (!user || (userProfile?.role !== 'studio_admin')) {
      navigate('/');
      return;
    }

    loadRevenueData();
  }, [user, userProfile, navigate]);

  const loadRevenueData = async () => {
    setLoading(true);
    try {
      // This would load revenue data from the API
      // For now, we'll use placeholder data
      setRevenueData({
        totalRevenue: 0,
        monthlyRevenue: 0,
        adRevenue: 0,
        iapRevenue: 0,
        payouts: []
      });
    } catch (error) {
      console.error('Failed to load revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading revenue data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StudioSidebar />
      
      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold text-foreground">Revenue</h1>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Revenue & Payouts
              </h1>
              <p className="text-muted-foreground">
                Track your earnings and payout history
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${revenueData.totalRevenue.toFixed(2)}
                  </p>
                </div>
                <Icon name="DollarSign" size={24} className="text-primary" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${revenueData.monthlyRevenue.toFixed(2)}
                  </p>
                </div>
                <Icon name="TrendingUp" size={24} className="text-accent" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ad Revenue</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${revenueData.adRevenue.toFixed(2)}
                  </p>
                </div>
                <Icon name="Play" size={24} className="text-secondary" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">IAP Revenue</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${revenueData.iapRevenue.toFixed(2)}
                  </p>
                </div>
                <Icon name="ShoppingCart" size={24} className="text-primary" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-4">Payout History</h3>
            
            {revenueData.payouts.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h4 className="text-lg font-medium text-foreground mb-2">No Payouts Yet</h4>
                <p className="text-muted-foreground">
                  Payouts are processed monthly once you reach the minimum threshold
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {revenueData.payouts.map((payout, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">${payout.amount}</p>
                      <p className="text-sm text-muted-foreground">{payout.period}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      payout.status === 'paid' ? 'bg-green-100 text-green-800' :
                      payout.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {payout.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 mb-2">Revenue Sharing Information</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Payouts are processed monthly via Stripe</li>
                  <li>• Minimum payout threshold: $50</li>
                  <li>• Revenue share varies based on game performance</li>
                  <li>• Ad revenue is shared based on impressions and engagement</li>
                </ul>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StudioRevenue;
